import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2, Play } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/aplicativos-iptv/ibo-player")({
  head: () => ({
    meta: [
      { title: "Como Baixar e Instalar o IBO Player na Samsung, LG, Android, TV Box e iPhone | P2 PRO" },
      { name: "description", content: "Aprenda como baixar e instalar o aplicativo IBO Player na sua Smart TV Samsung, LG, Android, TV Box ou iPhone. Guia completo passo a passo atualizado em 2026." },
    ],
  }),
  component: IboPlayerPage,
});

function IboPlayerPage() {
  const [showVideo, setShowVideo] = useState(false);

  return (
    <SEOPage
      category="Tutorial"
      title="Como Baixar e Instalar o IBO Player na Samsung, LG, Android, TV Box e iPhone"
      subtitle="IBO Player: Aplicativo Compatível com Painel IPTV Pós Pago"
      introParagraphs={[
        "O IBO Player é um dos aplicativos mais populares para Smart TVs e dispositivos móveis, oferecendo uma interface moderna, rápida e fácil de utilizar. Compatível com Samsung Smart TV, LG Smart TV, Android, TV Box e iPhone, o aplicativo é amplamente utilizado por usuários que procuram praticidade e estabilidade.",
        "Confira abaixo o nosso tutorial em vídeo completo e o guia passo a passo para instalação em todos os seus dispositivos."
      ]}
      sections={[
        {
          title: "Tutorial em Vídeo e Guia Visual",
          type: "featured",
          content: (
            <div className="space-y-8">
              <div className="aspect-video relative bg-black overflow-hidden rounded-[2rem] border border-white/10 shadow-2xl group cursor-pointer">
                {!showVideo ? (
                  <div 
                    className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#12141a] to-black"
                    onClick={() => setShowVideo(true)}
                  >
                    <div className="w-20 h-20 rounded-full bg-electric text-white flex items-center justify-center shadow-glow animate-pulse group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 fill-current" />
                    </div>
                    <p className="mt-6 text-sm font-black uppercase tracking-widest text-muted-foreground group-hover:text-electric transition-colors">Clique para assistir o tutorial</p>
                  </div>
                ) : (
                  <iframe
                    src="https://www.youtube.com/embed/M1rFy4Ei04g?autoplay=1&rel=0"
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Como Baixar e Instalar o IBO Player"
                  />
                )}
              </div>
              
              <div className="space-y-4">
                <h4 className="text-xl font-black uppercase tracking-tighter italic flex items-center gap-3">
                  <div className="w-2 h-8 bg-electric rounded-full" />
                  Como Baixar e Instalar o IBO Player
                </h4>
                <div className="rounded-[2rem] overflow-hidden border border-white/5 shadow-2xl bg-white/[0.02]">
                  <img 
                    src="https://wogunbzijppmeuleitjq.supabase.co/storage/v1/object/public/temp-images/36e86759-b005-4bb0-9d09-32124ff5c027.webp" 
                    alt="Guia de Instalação IBO Player" 
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          )
        },
        {
          title: "O Que é o IBO Player?",
          type: "default",
          content: (
            <div className="space-y-6 text-muted-foreground leading-relaxed">
              <p>
                O IBO Player é um aplicativo reprodutor desenvolvido para oferecer uma experiência fluida e organizada em Smart TVs e dispositivos móveis. Sua interface intuitiva facilita a navegação e a configuração, tornando-o uma das opções mais procuradas pelos usuários.
              </p>
              <p>
                Além disso, o IBO Player é compatível com sistemas profissionais de Painel IPTV Pós Pago, sendo amplamente utilizado por revendedores e provedores de streaming.
              </p>
            </div>
          )
        },
        {
          title: "Como Instalar o IBO Player na Samsung Smart TV",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Conecte sua Samsung Smart TV à internet." },
                  { step: 2, text: "Abra a loja de aplicativos Samsung." },
                  { step: 3, text: "Utilize a busca da loja." },
                  { step: 4, text: "Pesquise por: IBO Player" },
                  { step: 5, text: "Selecione o aplicativo oficial." },
                  { step: 6, text: "Clique em Instalar." },
                  { step: 7, text: "Aguarde a conclusão da instalação." },
                  { step: 8, text: "Abra o aplicativo para visualizar as informações de ativação." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Pronto! O IBO Player já estará instalado em sua Samsung Smart TV.</p>
            </div>
          )
        },
        {
          title: "Como Instalar o IBO Player na LG Smart TV",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Conecte sua TV à internet." },
                  { step: 2, text: "Abra a LG Content Store." },
                  { step: 3, text: "Utilize a pesquisa." },
                  { step: 4, text: "Digite: IBO Player" },
                  { step: 5, text: "Selecione o aplicativo oficial." },
                  { step: 6, text: "Clique em Instalar." },
                  { step: 7, text: "Aguarde a instalação." },
                  { step: 8, text: "Abra o aplicativo para visualizar os dados de ativação." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Agora o IBO Player está pronto para uso em sua LG Smart TV.</p>
            </div>
          )
        },
        {
          title: "Como Baixar o IBO Player no Android",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Abra a Google Play Store." },
                  { step: 2, text: "Pesquise por: IBO Player" },
                  { step: 3, text: "Selecione o aplicativo oficial." },
                  { step: 4, text: "Clique em Instalar." },
                  { step: 5, text: "Aguarde a conclusão do download." },
                  { step: 6, text: "Abra o aplicativo." },
                  { step: 7, text: "Configure seus dados de acesso." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Pronto! O IBO Player estará disponível em seu dispositivo Android.</p>
            </div>
          )
        },
        {
          title: "Como Instalar o IBO Player em TV Box",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Abra a Play Store da TV Box." },
                  { step: 2, text: "Pesquise por: IBO Player" },
                  { step: 3, text: "Selecione o aplicativo." },
                  { step: 4, text: "Clique em Instalar." },
                  { step: 5, text: "Abra o aplicativo após a instalação." },
                  { step: 6, text: "Realize a configuração utilizando seus dados de acesso." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Agora o IBO Player está pronto para uso em sua TV Box.</p>
            </div>
          )
        },
        {
          title: "Como Baixar o IBO Player no iPhone",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Abra a App Store." },
                  { step: 2, text: "Pesquise por: IBO Player" },
                  { step: 3, text: "Selecione o aplicativo oficial." },
                  { step: 4, text: "Toque em Obter." },
                  { step: 5, text: "Aguarde a instalação." },
                  { step: 6, text: "Abra o aplicativo." },
                  { step: 7, text: "Realize a configuração utilizando seus dados de acesso." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Pronto! Agora você pode utilizar o IBO Player em seu iPhone.</p>
            </div>
          )
        },
        {
          title: "Como Configurar o IBO Player",
          type: "featured",
          content: (
            <div className="space-y-6">
              <p className="text-muted-foreground">Após instalar o aplicativo, siga estas etapas finais:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Abra o IBO Player." },
                  { step: 2, text: "Anote o endereço MAC e o Device Key exibidos na tela." },
                  { step: 3, text: "Informe esses dados ao seu revendedor ou administrador." },
                  { step: 4, text: "Após a configuração ser realizada, reinicie o aplicativo." },
                  { step: 5, text: "Aguarde o carregamento das informações." },
                ].map((item) => (
                  <div key={item.step} className="flex items-center gap-4 p-4 rounded-xl bg-electric/5 border border-electric/10">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-electric text-white flex items-center justify-center font-black text-sm">
                      {item.step}
                    </span>
                    <p className="text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Pronto! Seu aplicativo estará configurado.</p>
            </div>
          )
        },
        {
          title: "Dispositivos Compatíveis com o IBO Player",
          type: "default",
          content: (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Samsung Smart TV",
                "LG Smart TV",
                "Android TV / Box",
                "iPhone / iPad"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/5">
                  <CheckCircle2 className="w-5 h-5 text-electric shrink-0" />
                  <span className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">{item}</span>
                </div>
              ))}
            </div>
          )
        },
        {
          title: "Principais Benefícios do IBO Player",
          type: "default",
          content: (
            <div className="grid sm:grid-cols-2 gap-4 text-muted-foreground">
              {[
                "Interface moderna",
                "Excelente desempenho",
                "Fácil configuração",
                "Compatibilidade com Smart TVs",
                "Compatibilidade com Android e iPhone",
                "Atualizações frequentes",
                "Navegação intuitiva",
                "Integração com Painel IPTV Pós Pago"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                  <span className="font-medium">{item}</span>
                </div>
              ))}
            </div>
          )
        },
        {
          title: "IBO Player Compatível com Painel IPTV Pós Pago",
          type: "default",
          content: (
            <div className="space-y-6 text-muted-foreground leading-relaxed text-lg italic">
              <p>
                O IBO Player é amplamente utilizado por usuários e revendedores que trabalham com Painel IPTV Pós Pago. Sua estabilidade e compatibilidade tornam o aplicativo uma excelente escolha para quem busca uma experiência profissional.
              </p>
              <p>
                Além disso, ele funciona perfeitamente com estruturas modernas voltadas para gerenciamento de acessos e revendas.
              </p>
            </div>
          )
        },
        {
          title: "Outros Aplicativos Compatíveis",
          type: "default",
          content: (
            <div className="space-y-4">
              <p className="text-muted-foreground">Além do IBO Player, nossa estrutura oferece suporte para:</p>
              <ul className="grid gap-3 list-none p-0">
                {["One X", "VuPlayer", "Blessed", "Fun Play", "Lazer Play", "P2PRO", "P2PLAY", "Prime Play"].map((item) => (
                  <li key={item} className="flex items-center gap-3 m-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )
        }
      ]}
      benefits={[
        { title: "Design Moderno", description: "Uma das interfaces mais bonitas do mercado." },
        { title: "Performance", description: "Navegação rápida e carregamento instantâneo." },
        { title: "Pós Pago", description: "Totalmente compatível com nossa estrutura de revenda." }
      ]}
      howItWorks={[
        { title: "Instalação", description: "Baixe o IBO Player na loja oficial da sua TV ou smartphone." },
        { title: "Ativação", description: "Envie o MAC e Device Key para o seu revendedor." },
        { title: "Acesso", description: "Aproveite a melhor experiência em streaming." }
      ]}
      diferenciais={[
        { title: "Favoritos", description: "Organize seus canais e filmes favoritos com facilidade." },
        { title: "Multi-Telas", description: "Compatível com diversos tamanhos de tela e resoluções." },
        { title: "Suporte 4K", description: "Aproveite a máxima definição de imagem." }
      ]}
      faq={[
        { question: "O IBO Player funciona na Samsung Smart TV?", answer: "Sim. O aplicativo está disponível para TVs Samsung compatíveis." },
        { question: "O IBO Player funciona na LG Smart TV?", answer: "Sim. O aplicativo pode ser encontrado na LG Content Store." },
        { question: "O IBO Player funciona em Android?", answer: "Sim. O aplicativo está disponível para Android, Android TV e TV Box." },
        { question: "O IBO Player funciona no iPhone?", answer: "Sim. O aplicativo pode ser baixado diretamente pela App Store." },
        { question: "Preciso configurar o aplicativo após a instalação?", answer: "Sim. É necessário informar os dados exibidos pelo aplicativo para concluir a configuração." },
        { question: "O IBO Player é compatível com Painel IPTV Pós Pago?", answer: "Sim. O aplicativo funciona perfeitamente com sistemas profissionais de Painel IPTV Pós Pago." }
      ]}
      breadcrumbs={[
        { label: "Aplicativos", href: "/aplicativos-iptv" },
        { label: "IBO Player", href: "/aplicativos-iptv/ibo-player" }
      ]}
      ctaTitle="🚀 Tenha Seu Próprio Painel IPTV Pós Pago"
      ctaDescription="Comece hoje mesmo com uma estrutura profissional preparada para escalar suas vendas. IBO Player, One X, VuPlayer e muito mais."
      ctaButtonText="QUERO TESTAR O PAINEL IPTV PÓS PAGO AGORA"
    />
  );
}